import type { SubmitRunOutcome } from "./LeaderboardService";
import type { MyLevelRank, ServiceResult } from "./types";

export interface NamePromptDecision {
  shouldPrompt: boolean;
  /** The player's rank on this level, when known - present even when `shouldPrompt` is false (e.g. an already-named player still has a real rank), `null` only when it was never determined. */
  rank: number | null;
}

/**
 * Pure decision logic for "should this completed run's result open the
 * NEW HIGH SCORE name-entry prompt" - the entire Top-10/name-prompt
 * contract in one small, framework-free function, extracted out of
 * `GameScene` so its rules are unit-testable without Phaser, a Scene, or
 * a network call:
 *
 *  - Only a confirmed, this-call synchronization (`submitOutcome ===
 *    "sent"`) counts. `"queued"` (offline, or a transient failure -
 *    still pending a later retry) and `"disabled"` must never claim a
 *    Top 10 result the server hasn't actually recorded yet.
 *  - Rank 1-10 (inclusive) with a real score prompts; rank 11+ never
 *    does. An empty leaderboard naturally makes the first successful
 *    player rank 1 - no special-casing needed, it falls out of the same
 *    rank check.
 *  - A player who already has a display name is never asked again,
 *    regardless of rank.
 */
export function decideNamePrompt(
  submitOutcome: SubmitRunOutcome,
  rankResult: ServiceResult<MyLevelRank>,
  nameResult: ServiceResult<string | null>,
): NamePromptDecision {
  if (submitOutcome !== "sent") return { shouldPrompt: false, rank: null };
  if (rankResult.state !== "ok" || !rankResult.data.hasScore || rankResult.data.rank === null) {
    return { shouldPrompt: false, rank: null };
  }
  const rank = rankResult.data.rank;
  if (rank > 10) return { shouldPrompt: false, rank };
  // A resolved, non-empty name means the player already has one - never ask again.
  if (nameResult.state !== "ok" || nameResult.data) return { shouldPrompt: false, rank };
  return { shouldPrompt: true, rank };
}
